import { useEffect, useState } from 'react';
import axios from 'axios';
import { Download, Save, Loader2, Scale, Inbox, Globe, EyeOff } from 'lucide-react';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Badge } from './ui/badge';
import { Switch } from './ui/switch';
import { toast } from 'sonner';
import { API } from '../config/api';

const TYPE_LABELS = {
  retractation: 'Rétractation', resiliation: 'Résiliation', suppression: 'Suppression de compte',
};

const LegalPublishSwitch = () => {
  const [settings, setSettings] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    axios.get(`${API}/admin/legal/settings`, { withCredentials: true })
      .then(r => setSettings(r.data)).catch(() => toast.error('Erreur de chargement'));
  }, []);

  const toggle = async (next) => {
    const msg = next
      ? 'Publier les pages légales ? Elles deviendront visibles par TOUTES les visiteuses (pied de page, case CGV à l\'inscription, liens).'
      : 'Masquer les pages légales au public ? Elles resteront visibles pour vous (admin) et en preview.';
    if (!window.confirm(msg)) return;
    setBusy(true);
    try {
      const r = await axios.put(`${API}/admin/legal/settings`, { published: next }, { withCredentials: true });
      setSettings(r.data);
      toast.success(next ? 'Pages légales publiées' : 'Pages légales masquées au public');
    } catch (e) { toast.error(e.response?.data?.detail || 'Erreur'); }
    finally { setBusy(false); }
  };

  if (!settings) return null;
  const isPreview = settings.env === 'preview';
  return (
    <Card className="bg-white border-[#E9DDCC] rounded-3xl" data-testid="legal-publish-card">
      <CardContent className="p-5 flex flex-wrap items-center justify-between gap-4">
        <div className="flex items-start gap-3 min-w-0">
          {settings.published ? <Globe className="w-5 h-5 text-[#9A744C] mt-0.5" strokeWidth={1.5} /> : <EyeOff className="w-5 h-5 text-[#C76542] mt-0.5" strokeWidth={1.5} />}
          <div>
            <h3 className="font-heading text-lg text-[#493329]">Pages légales publiées</h3>
            <p className="text-xs text-[#7A6455] mt-1" data-testid="legal-publish-status">
              {settings.published
                ? 'ON — visibles par toutes les visiteuses (pied de page, case CGV à l\'inscription, liens).'
                : 'OFF — masquées au public en production : pied de page, case CGV et liens absents, pages indisponibles. Visibles uniquement pour les admins connectés.'}
            </p>
            <p className="text-[11px] text-[#AE9885] mt-1" data-testid="legal-publish-env">
              Environnement actuel : <strong>{isPreview ? 'PREVIEW' : 'PRODUCTION'}</strong>
              {isPreview && ' — en preview, les pages sont toujours visibles pour vos tests, quel que soit l\'interrupteur.'}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <span className="text-xs text-[#7A6455]">{settings.published ? 'Publié' : 'Masqué'}</span>
          <Switch checked={settings.published} disabled={busy} onCheckedChange={toggle}
            className="data-[state=checked]:bg-[#C99A55]" data-testid="legal-publish-switch" />
        </div>
      </CardContent>
    </Card>
  );
};

export const LegalAdminPanel = () => {
  const [docs, setDocs] = useState([]);
  const [selected, setSelected] = useState(null);
  const [title, setTitle] = useState('');
  const [content, setContent] = useState('');
  const [versions, setVersions] = useState([]);
  const [requests, setRequests] = useState([]);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    try {
      const [d, r] = await Promise.all([
        axios.get(`${API}/admin/legal`, { withCredentials: true }),
        axios.get(`${API}/admin/rgpd/requests`, { withCredentials: true }),
      ]);
      setDocs(d.data.documents || []);
      setRequests(r.data.requests || []);
    } catch { toast.error('Erreur de chargement'); }
  };
  useEffect(() => { load(); }, []);

  const openDoc = async (doc) => {
    setSelected(doc.doc_type); setTitle(doc.title); setContent(doc.content);
    try {
      const v = await axios.get(`${API}/admin/legal/${doc.doc_type}/versions`, { withCredentials: true });
      setVersions(v.data.versions || []);
    } catch { setVersions([]); }
  };

  const save = async () => {
    if (!window.confirm(`Publier une nouvelle version de « ${title} » ? La version actuelle sera archivée (PDF conservé).`)) return;
    setSaving(true);
    try {
      const r = await axios.put(`${API}/admin/legal/${selected}`, { title, content }, { withCredentials: true });
      toast.success(`Version ${r.data.version} publiée`);
      setSelected(null); load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Erreur'); }
    finally { setSaving(false); }
  };

  const closeRequest = async (req) => {
    const note = window.prompt('Note de traitement (trace RGPD) :', '');
    if (note === null) return;
    try {
      await axios.post(`${API}/admin/rgpd/requests/${req.id}/close`, { note, status: 'traitee' }, { withCredentials: true });
      toast.success('Demande traitée (trace conservée)'); load();
    } catch { toast.error('Erreur'); }
  };

  const pending = requests.filter(r => r.status === 'en_attente');

  return (
    <div className="space-y-5" data-testid="legal-admin-panel">
      <LegalPublishSwitch />
      <Card className="bg-white border-[#E9DDCC] rounded-3xl">
        <CardContent className="p-5">
          <div className="flex items-center gap-2 mb-4">
            <Scale className="w-4 h-4 text-[#C99A55]" strokeWidth={1.5} />
            <h3 className="font-heading text-lg text-[#493329]">Pages légales (éditeur versionné)</h3>
          </div>
          {!selected ? (
            <div className="grid sm:grid-cols-2 gap-3">
              {docs.map(d => (
                <button key={d.doc_type} onClick={() => openDoc(d)} data-testid={`legal-edit-${d.doc_type}`}
                  className="text-left p-4 rounded-2xl border border-[#E9DDCC] hover:border-[#C99A55] transition-colors">
                  <p className="text-sm font-medium text-[#493329]">{d.title}</p>
                  <p className="text-[11px] text-[#AE9885] mt-1">
                    v{d.version} · {new Date(d.updated_at).toLocaleDateString('fr-FR')}
                    {d.content.includes('[Texte à fournir') && <span className="ml-2 text-[#C76542]">⚠ texte à fournir</span>}
                  </p>
                </button>
              ))}
            </div>
          ) : (
            <div className="space-y-3">
              <Input value={title} onChange={e => setTitle(e.target.value)} className="rounded-xl border-[#E9DDCC]" data-testid="legal-title-input" />
              <Textarea value={content} onChange={e => setContent(e.target.value)} rows={14}
                className="rounded-xl border-[#E9DDCC] text-sm font-mono" data-testid="legal-content-input" />
              <div className="flex flex-wrap gap-2">
                <Button onClick={save} disabled={saving} className="rounded-full bg-[#C99A55] hover:bg-[#9A744C] text-white" data-testid="legal-save-btn">
                  {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Save className="w-4 h-4 mr-2" /> Publier (nouvelle version)</>}
                </Button>
                <Button variant="outline" onClick={() => setSelected(null)} className="rounded-full border-[#E9DDCC] text-[#7A6455]">Annuler</Button>
              </div>
              <div className="pt-2 border-t border-[#E9DDCC]">
                <p className="text-xs text-[#AE9885] mb-2">Historique des versions (PDF archivé pour chacune) :</p>
                <div className="flex flex-wrap gap-2">
                  {versions.map(v => (
                    <a key={v.version} href={`${API}/legal/${selected}/pdf?version=${v.version}`} target="_blank" rel="noopener noreferrer">
                      <Badge variant="outline" className="rounded-full border-[#E9DDCC] text-[#7A6455] cursor-pointer hover:border-[#C99A55]">
                        <Download className="w-3 h-3 mr-1" /> v{v.version}{v.current ? ' (actuelle)' : ''}
                      </Badge>
                    </a>
                  ))}
                </div>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      <Card className="bg-white border-[#E9DDCC] rounded-3xl">
        <CardContent className="p-5">
          <div className="flex items-center gap-2 mb-4">
            <Inbox className="w-4 h-4 text-[#C99A55]" strokeWidth={1.5} />
            <h3 className="font-heading text-lg text-[#493329]">Demandes RGPD & résiliations</h3>
            {pending.length > 0 && <Badge className="bg-[#C76542] text-white rounded-full">{pending.length} en attente</Badge>}
          </div>
          {requests.length === 0 ? (
            <p className="text-sm text-[#AE9885]">Aucune demande pour le moment.</p>
          ) : (
            <div className="space-y-2" data-testid="rgpd-requests-list">
              {requests.map(r => (
                <div key={r.id} className="flex items-start justify-between gap-3 p-3 rounded-2xl border border-[#E9DDCC]">
                  <div className="min-w-0">
                    <p className="text-sm text-[#493329]">
                      <span className="font-medium">{TYPE_LABELS[r.request_type] || r.request_type}</span> — {r.name} ({r.email})
                    </p>
                    <p className="text-[11px] text-[#AE9885]">
                      {new Date(r.created_at).toLocaleString('fr-FR')}
                      {r.legitimate_motive && ` · Motif légitime : ${r.reason_type}`}
                      {r.reason_text && ` · « ${r.reason_text} »`}
                      {r.admin_note && ` · Note : ${r.admin_note}`}
                    </p>
                  </div>
                  {r.status === 'en_attente' ? (
                    <Button size="sm" onClick={() => closeRequest(r)} className="rounded-full bg-[#C99A55] hover:bg-[#9A744C] text-white text-xs shrink-0" data-testid={`rgpd-close-${r.id}`}>
                      Traiter
                    </Button>
                  ) : (
                    <Badge variant="outline" className="rounded-full border-[#E9DDCC] text-[#AE9885] shrink-0">Traitée</Badge>
                  )}
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};
